/**
 * Wallet helper — thin ethers@6 bridge for connecting MetaMask-style
 * browser wallets, plus a mock variant for demo mode so the
 * sign-in flow never actually prompts a wallet extension.
 */

import { BrowserProvider, type JsonRpcSigner } from "ethers";

export interface MinimalSigner {
  /** 0x-prefixed ETH address. */
  address: string;
  /** EIP-191 personal_sign — returns 0x-prefixed hex signature. */
  signMessage(message: string): Promise<string>;
  /** Underlying ethers signer for contract writes. `null` in demo/mock mode. */
  ethersSigner?: JsonRpcSigner | null;
  /** Underlying ethers provider for reads. `null` in demo/mock mode. */
  ethersProvider?: BrowserProvider | null;
}

function randomHex(length: number): string {
  const bytes = new Uint8Array(length);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Prompt the in-browser wallet (window.ethereum) to connect and
 * return the primary account + signer. Throws if no provider.
 */
export async function connectBrowserWallet(): Promise<{ address: string; signer: MinimalSigner }> {
  if (typeof window === "undefined" || !(window as any).ethereum) {
    throw new Error("No browser wallet found. Install MetaMask or an EIP-1193 provider.");
  }
  const provider = new BrowserProvider((window as any).ethereum);
  // Trigger the accounts prompt if needed.
  await provider.send("eth_requestAccounts", []);
  const signer: JsonRpcSigner = await provider.getSigner();
  const address = await signer.getAddress();
  return {
    address,
    signer: {
      address,
      signMessage: (m) => signer.signMessage(m),
      ethersSigner: signer,
      ethersProvider: provider,
    },
  };
}

/**
 * Demo-mode wallet — returns a random 0x address and a stub signer
 * that produces a deterministic fake signature. No external prompt.
 */
export function mockConnectWallet(): { address: string; signer: MinimalSigner } {
  const address = "0x" + randomHex(20);
  return {
    address,
    signer: {
      address,
      signMessage: async (m) => {
        // Fake signature derived from the message so repeat calls
        // are stable. Not cryptographically meaningful.
        const base = typeof btoa === "function" ? btoa(m).slice(0, 32) : "demo-sig";
        return "0x" + Array.from(base).map((c) => c.charCodeAt(0).toString(16)).join("");
      },
      ethersSigner: null,
      ethersProvider: null,
    },
  };
}
